// axiosInstance.ts
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { AgencyNotSetError, getApiBaseUrl } from '../../../shared/config/agencyUrl';
import { authStorage } from '../../../features/auth/api/authStorage';
import { userIdStorage } from '../../../features/auth/api/userIdStorage';
import { agencyStorage } from '../../../features/auth/api/agencyStorage';
import { timeZoneStorage } from '../../../features/auth/api/timeZoneStorage';
import store from '../../../shared/store';
import { setIsNetworkError, showErrorModal, showSessionExpiredMessage } from '../../../shared/store/errorSlice';
import { refreshAccessToken } from '../services/authProvider';
import { SESSION_EXPIRED_MSG } from '../constants/authConstants';

let onSessionExpired: (() => void) | null = null;

export const setSessionExpiredCallback = (cb: () => void) => {
  onSessionExpired = cb;
};

// Manages the promise of a failed network request, allowing it to be retried.
export const networkErrorManager = {
  requestQueue: [] as {
    request: InternalAxiosRequestConfig;
    promise: {
      resolve: (value: any) => void;
      reject: (reason?: any) => void;
    };
  }[],

  storeRequestAndPromise(
    req: InternalAxiosRequestConfig,
    res: (value: any) => void,
    rej: (reason?: any) => void,
  ) {
    this.requestQueue.push({
      request: req,
      promise: { resolve: res, reject: rej },
    });
  },

  retryAllRequests() {
    const promises = this.requestQueue.map(({ request, promise }) =>
      api(request).then(promise.resolve).catch(promise.reject),
    );
    this.clear();
    return Promise.all(promises);
  },

  rejectAllRequests(reason: any) {
    this.requestQueue.forEach(({ promise }) => {
      promise.reject(reason);
    });
    this.clear();
  },

  clear() {
    this.requestQueue = [];
  },
};

// Tracks transient-retry counts per request, keyed by "<method>:<url>".
// Using a stable string key avoids the WeakMap pitfall where Axios creates
// a new config object for each outgoing attempt, making the previous WeakMap
// key unreachable. Entries are removed once retries are exhausted or succeed.
const transientRetryMap = new Map<string, number>();

// Flag to prevent concurrent refresh attempts triggering multiple logouts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

const onTokenRefreshed = (token: string | null) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// No baseURL here: it depends on the agency, which is not known when this
// module loads. The request interceptor resolves it for every request.
const api: AxiosInstance = axios.create({
  timeout: 30000,
  withCredentials: true, // Required for TTAuth cookie session on web; harmless for Entra
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Response-interceptor helpers (module-level)
// Extracted to reduce the cognitive complexity of the error handler from 42
// to ≤15 (SonarQube S3776). All module-level variables (isRefreshing, api,
// authStorage, store, …) are accessible from these functions.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Handles Axios errors where no response was received from the server
 * (network error, timeout, ECONNABORTED, etc.).
 */
async function handleNetworkError(error: AxiosError<any>): Promise<any> {
  // Timeout — reject immediately; the caller handles these separately.
  if (
    error.code === 'ECONNABORTED' ||
    error.message?.toLowerCase().includes('timeout')
  ) {
    throw error;
  }

  // Android CustomTabsIntent transient retry (exponential back-off).
  if (error.config) {
    const retryKey = `${error.config.method ?? 'get'}:${error.config.url ?? ''}`;
    const attempt = transientRetryMap.get(retryKey) ?? 0;
    const DELAYS = [500, 1000, 2000];
    if (attempt < DELAYS.length) {
      transientRetryMap.set(retryKey, attempt + 1);
      const cfg = error.config;
      return new Promise<any>((resolve, reject) => {
        setTimeout(() => {
          api(cfg)
            .then(res => {
              transientRetryMap.delete(retryKey);
              resolve(res);
            })
            .catch(error_ => {
              if ((transientRetryMap.get(retryKey) ?? 0) >= DELAYS.length) {
                transientRetryMap.delete(retryKey);
              }
              reject(error_);
            });
        }, DELAYS[attempt]);
      });
    }
    transientRetryMap.delete(retryKey);
  }

  return new Promise((resolve, reject) => {
    networkErrorManager.storeRequestAndPromise(
      error.config as InternalAxiosRequestConfig,
      resolve,
      reject,
    );
    store.dispatch(setIsNetworkError({ isNetworkError: true }));
    store.dispatch(
      showErrorModal({
        title: 'common.networkError.title',
        subTitle: 'common.networkError.subTitle',
        message: 'common.networkError.desc',
        ctas: [{ label: 'common.networkError.tryAgaincta', type: 'RETRY' }],
        screen: '',
        noTranslation: false,
      }),
    );
  });
}

/**
 * Handles 401 Unauthorized errors: attempts a silent token refresh, queues
 * concurrent requests, and retries on success.
 */
async function handle401Error(
  error: AxiosError<any>,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  isPinVerifyRequest: boolean,
): Promise<any> {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshSubscribers.push((token: string | null) => {
        if (token) {
          const isSentinel = token === 'ttauth-cookie-session';
          if (!isSentinel) {
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
          }
          resolve(api(originalRequest));
        } else {
          reject(error);
        }
      });
    });
  }

  isRefreshing = true;
  try {
    console.log(
      `[axiosInstance] 401 received${isPinVerifyRequest ? ' (verify-pin — refreshing session before retry)' : ''} — attempting token refresh`,
    );
    const newToken = await refreshAccessToken();
    isRefreshing = false;

    if (newToken) {
      const isSentinel = newToken === 'ttauth-cookie-session';
      if (isSentinel) {
        console.log('[axiosInstance] TTAuth cookie session refreshed — retrying original request via cookie');
        originalRequest.headers.delete('Authorization');
      } else {
        console.log('[axiosInstance] Token refreshed — retrying original request with new Bearer token');
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
      }
      onTokenRefreshed(newToken);
      return api(originalRequest);
    }

    console.warn('[axiosInstance] Token refresh returned null — session expired');
    onTokenRefreshed(null);
    await authStorage.clear();
    onSessionExpired?.();
    // Re-throw the original 401 so the caller's promise rejects (matching the
    // original fall-through behaviour where the outer handler called
    // Promise.reject at the bottom after onSessionExpired).
    throw error;
  } catch (refreshError) {
    isRefreshing = false;
    const isNetworkErr =
      refreshError instanceof TypeError ||
      (refreshError instanceof Error &&
        (refreshError.message?.toLowerCase().includes('network') ||
          refreshError.message?.toLowerCase().includes('fetch') ||
          refreshError.message?.toLowerCase().includes('failed to fetch') ||
          refreshError.message?.toLowerCase().includes('network request failed')));

    if (isNetworkErr) {
      console.warn('[axiosInstance] Token refresh failed due to network error — not signing out:', refreshError);
      onTokenRefreshed(null);
      throw error;
    }

    console.warn('[axiosInstance] Token refresh threw a non-network error — session expired:', refreshError);
    onTokenRefreshed(null);
    await authStorage.clear();
    onSessionExpired?.();
    // Re-throw the original 401 so the caller's promise rejects (matching the
    // original fall-through behaviour).
    throw error;
  }
}

/* ---------------- REQUEST INTERCEPTOR ---------------- */
api.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    // {agency}.fastertech.ai — the subdomain on web, the entered agency on
    // mobile. Throws AgencyNotSetError when there is none yet, which rejects
    // the request before anything is sent to a wrong or half-built host.
    if (!config.baseURL) {
      config.baseURL = getApiBaseUrl();
    }

    const token = await authStorage.getAccessToken();

    // Never send the TTAuth cookie-only sentinel as a Bearer token.
    // When /cookie/me returns no body token, ttAuthAdapter stores the sentinel
    // 'ttauth-cookie-session' as a navigation trigger only. BFF authentication
    // for that flow relies on the HttpOnly cookie (withCredentials:true).
    // When /cookie/me returns a real JWT, it is stored directly and sent here.
    const isTTAuthSentinel = token === 'ttauth-cookie-session';
    if (token && !isTTAuthSentinel) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    const userId = userIdStorage.get();
    if (userId !== null) {
      config.headers.set('X-UserId', String(userId));
    }

    // X-Agency-Id carries the agency name the user entered (e.g. "Transit-Local").
    // The BFF uses this to identify the tenant; no separate agencyCode is returned
    // by the validate endpoint — the name itself is the identifier.
    const agencyId = agencyStorage.get();
    if (agencyId !== null) {
      config.headers.set('X-Agency-Id', agencyId);
    }

    const timeZoneId = timeZoneStorage.get();
    if (timeZoneId !== null) {
      config.headers.set('X-TimeZone', timeZoneId);
    }

    return config;
  },
  (error: unknown) =>
    Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

/* ---------------- RESPONSE INTERCEPTOR ---------------- */
api.interceptors.response.use(
  response => {
    // Detect session-expired message returned inside a successful (2xx) response body.
    if (response?.data?.message?.includes(SESSION_EXPIRED_MSG)) {
      store.dispatch(showSessionExpiredMessage());
    }
    return response;
  },
  async (error: AxiosError<any>) => {
    // Raised by the request interceptor before anything was sent: there is no
    // agency to build the URL from. Not a network problem, so no retries and
    // no network modal — the caller decides, and the user is sent back to
    // AgencySelectionScreen by the normal auth flow.
    if ((error as unknown) instanceof AgencyNotSetError) {
      return Promise.reject(error);
    }

    // Network errors (no response) — delegate to helper to cap complexity.
    if (!error.response) {
      return handleNetworkError(error);
    }

    store.dispatch(setIsNetworkError({ isNetworkError: false }));

    // Handle session expired (401) — attempt silent token refresh first.
    // /api/auth/validate returning 401 is non-fatal (BFF token-type mismatch)
    // so we skip refresh for that endpoint entirely.
    const isPinVerifyRequest = error.config?.url?.includes('verify-pin') ?? false;
    const isValidateRequest = error.config?.url?.includes('auth/validate') ?? false;

    if (error.response.status === 401 && !isValidateRequest && error.config) {
      const originalRequest: InternalAxiosRequestConfig & { _retry?: boolean } = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        return handle401Error(error, originalRequest, isPinVerifyRequest);
      } else if (isPinVerifyRequest) {
        // Already retried once — for verify-pin this means wrong PIN (not expired); not signing out.
        console.warn('[axiosInstance] verify-pin returned 401 after session refresh — wrong PIN, not signing out');
      } else {
        // For all other endpoints it means session is genuinely expired.
        await authStorage.clear();
        onSessionExpired?.();
      }
    }

    if (error.response.status === 404 || error.response.status === 403) {
      return error.response;
    }

    const responseMessage =
      error.response.data?.message || error.message || 'Something went wrong';

    if (responseMessage?.includes(SESSION_EXPIRED_MSG)) {
      store.dispatch(showSessionExpiredMessage());
    }

    throw Object.assign(new Error(responseMessage), {
      status: error.response.status,
      message: responseMessage,
      data: error.response.data,
    });
  },
);

export default api;
